import React, { FunctionComponent } from 'react';

import { connect } from 'react-redux';
import { Translate } from 'react-localize-redux';

import moment from 'moment';

import { darken } from 'polished';
import styled from 'styled-components';
import { motion } from 'framer-motion';

// Components
import Flex from '@components/layout/flex';
import { getWidth } from '@components/styles';
import BackIcon from '@components/basics/icons/back';
import Datepicker from '@components/form/datepicker';
import Typography from '@components/basics/typography';
import DownloadIcon from '@components/basics/icons/download';
import { useLocation } from 'react-router-dom';
import { Link } from 'react-router-dom';

interface SummaryDetailsCtrlProps {
  className?: string;
  sectionTitle?: React.ReactNode;
  selectedDate: Date;
  hasDateChange: boolean;
  handleDownload: () => void;
  setHeader: () => void;
  handleDateChange: (value: string) => void;
  walletHistory: boolean;
  datePickerValue?: string;
  allTime: boolean;
  hasData: boolean;
}

const SummaryDetailsCtrl: FunctionComponent<SummaryDetailsCtrlProps> = ({
  className,
  children,
  sectionTitle,
  selectedDate,
  hasDateChange,
  handleDateChange,
  handleDownload,
  datePickerValue,
  hasData,
}) => {
  const { state } = useLocation<{ range?: string }>();
  const [range, setRange] = React.useState<unknown>();
  React.useEffect(() => {
    if (state) {
      setRange(state);
    }
  }, [state]);
  const getMinDate = () => moment('2020-01-01');
  const getMaxDate = () => moment().startOf('day').add(1, 'days');

  return (
    <Translate>
      {({ translate }) => (
        <div className={className || ''}>
          <HeaderMobile justifyContent="space-between" alignItems="center">
            <Link
              to={{
                pathname: '/earnings',
                state: range,
              }}
            >
              <BackIcon />
            </Link>
          </HeaderMobile>
          <Wrapper>
            <>
              <Typography
                className="wrapper-title"
                type="subheader"
                customFont="semi-bold"
              >
                {translate('EARNINGS.DETAILS.TITLE', {
                  section: sectionTitle,
                })}
              </Typography>
              <Flex
                className="wrapper-actions"
                justifyContent="space-between"
                alignItems="center"
              >
                <Flex
                  className="wrapper-filters"
                  justifyContent="flex-start"
                  alignItems="center"
                  gap={20}
                >
                  <DatesDropdown
                    hasPillShape
                    isMonthSelector
                    active={moment(selectedDate)}
                    onChange={(value) => handleDateChange(value)}
                    max={getMaxDate()}
                    min={getMinDate()}
                    hasChanges={hasDateChange}
                    value={datePickerValue}
                  />

                  {hasData && (
                    <CircleIcon
                      initial="rest"
                      whileHover="hover"
                      whileTap="click"
                      animate="rest"
                      exit="click"
                      onClick={() => handleDownload()}
                    >
                      <DownloadIcon />
                    </CircleIcon>
                  )}
                </Flex>
              </Flex>
            </>

            <BoxWrapper>{children}</BoxWrapper>
          </Wrapper>
        </div>
      )}
    </Translate>
  );
};

export default connect()(styled(SummaryDetailsCtrl)`
  margin-top: 0 auto;
  max-width: 1440px;
  min-height: 100vh;

  @media ${({ theme }) => theme.device.tablet} {
    margin: 0;
  }
`);

const Wrapper = styled.div`
  height: fit-content;
  .wrapper-title,
  .wrapper-actions {
    margin-bottom: 20px;
    @media ${({ theme }) => theme.device.tablet} {
      margin-bottom: 30px;
    }

    .wrapper-filters {
      width: 100%;
      display: flex;
      justify-content: space-between;
    }
  }
`;

const CircleIcon = styled(motion.div)`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  padding: 16px;
  background-color: ${({ theme }) => theme.colors.box};
  transition: background-color
    ${({ theme }) => `${theme.transitions.duration} ${theme.transitions.type}`};

  &:hover {
    background-color: ${({ theme }) => darken(0.05, theme.colors.midGray)};
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const HeaderMobile = styled(Flex)`
  margin-bottom: 20px;
  height: fit-content;
`;

const DatesDropdown = styled(Datepicker)`
  flex-grow: 1;
  @media ${({ theme }) => theme.device.tablet} {
    flex-grow: 0;
    min-width: ${getWidth(6)};
  }
`;

const BoxWrapper = styled.div`
  display: grid;
  grid-gap: 20px;
`;
